/**
 * AviationHub — public directory landing for airports + airlines.
 * Serves as the reference implementation for AirlinesAirports.com
 * inside the NewsPulse frontend.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, Building2, Search as SearchIcon, MapPin, Users, Newspaper } from "lucide-react";
import { npApi } from "./api";
import EventCard from "./EventCard";

function Pill({ children, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? "bg-slate-900 text-white"
          : "bg-white text-slate-700 border border-slate-200 hover:border-slate-400"
      }`}
    >
      {children}
    </button>
  );
}

function AirportCard({ airport }) {
  return (
    <Link
      to={`/aviation/airport/${airport.airport_id}`}
      className="group block rounded-xl border border-slate-200 bg-white p-5 hover:border-blue-400 hover:shadow-md transition-all"
      data-testid={`hub-airport-card-${airport.airport_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 border border-sky-100 text-sky-700 text-[11px] font-bold px-1.5 py-0.5">
              {airport.iata || "—"}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400">
              {airport.icao || ""}
            </span>
          </div>
          <h3 className="text-base font-bold text-slate-900 leading-tight group-hover:text-blue-700 transition-colors">
            {airport.canonical_name}
          </h3>
          <p className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {airport.city}, {airport.country_name || airport.country}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 p-2">
          <Plane className="w-4 h-4 text-white" strokeWidth={1.6} />
        </span>
      </div>
    </Link>
  );
}

function AirlineCard({ airline }) {
  const allianceLabel = ({
    star_alliance: "Star Alliance",
    oneworld: "oneworld",
    skyteam: "SkyTeam",
    none: null,
  })[airline.alliance || "none"];
  return (
    <Link
      to={`/aviation/airline/${airline.airline_id}`}
      className="group block rounded-xl border border-slate-200 bg-white p-5 hover:border-violet-400 hover:shadow-md transition-all"
      data-testid={`hub-airline-card-${airline.airline_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 rounded-md bg-violet-50 border border-violet-100 text-violet-700 text-[11px] font-bold px-1.5 py-0.5">
              {airline.iata || "—"}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400">
              {airline.icao || ""}
            </span>
          </div>
          <h3 className="text-base font-bold text-slate-900 leading-tight group-hover:text-violet-700 transition-colors">
            {airline.canonical_name}
          </h3>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {airline.country_name || airline.country}
            </span>
            {allianceLabel && (
              <>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> {allianceLabel}
                </span>
              </>
            )}
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-gradient-to-br from-violet-400 to-purple-600 p-2">
          <Building2 className="w-4 h-4 text-white" strokeWidth={1.6} />
        </span>
      </div>
    </Link>
  );
}

export default function NewsPulseAviationHub() {
  const [tab, setTab] = useState("airports");
  const [airports, setAirports] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [news, setNews] = useState([]);
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      npApi.airports({ limit: 500 }).catch(() => []),
      npApi.airlines({ limit: 500 }).catch(() => []),
      npApi.aviationFeed({ limit: 8 }).catch(() => []),
    ])
      .then(([ap, al, nw]) => {
        if (cancelled) return;
        setAirports(ap || []);
        setAirlines(al || []);
        setNews(nw || []);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const countries = useMemo(() => {
    const src = tab === "airports" ? airports : airlines;
    const set = new Map();
    for (const x of src) {
      if (x.country) set.set(x.country, x.country_name || x.country);
    }
    return Array.from(set.entries())
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [tab, airports, airlines]);

  const filteredAirports = useMemo(() => {
    const query = q.trim().toLowerCase();
    return airports.filter((a) => {
      if (country && a.country !== country) return false;
      if (!query) return true;
      return (
        (a.canonical_name || "").toLowerCase().includes(query) ||
        (a.iata || "").toLowerCase().includes(query) ||
        (a.icao || "").toLowerCase().includes(query) ||
        (a.city || "").toLowerCase().includes(query) ||
        (a.aliases || []).some((al) => al.toLowerCase().includes(query))
      );
    });
  }, [airports, country, q]);

  const filteredAirlines = useMemo(() => {
    const query = q.trim().toLowerCase();
    return airlines.filter((a) => {
      if (country && a.country !== country) return false;
      if (!query) return true;
      return (
        (a.canonical_name || "").toLowerCase().includes(query) ||
        (a.iata || "").toLowerCase().includes(query) ||
        (a.icao || "").toLowerCase().includes(query) ||
        (a.aliases || []).some((al) => al.toLowerCase().includes(query))
      );
    });
  }, [airlines, country, q]);

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-10" data-testid="aviation-hub">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800 p-8 md:p-12 text-white mb-8 relative overflow-hidden">
        <div className="absolute -right-8 -bottom-12 opacity-10">
          <Plane className="w-64 h-64" strokeWidth={0.6} />
        </div>
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.2em] text-sky-200 mb-2">
            AirlinesAirports.com · Aviation Knowledge Graph
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
            The canonical directory of the world's airports & airlines
          </h1>
          <p className="mt-3 text-sky-100 max-w-2xl">
            Browse authoritative profiles for every hub in our graph, powered by
            live NewsPulse ingestion. Each entity is linked to its routes,
            partner airlines, and the latest aviation stories that mention it.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <div className="rounded-lg bg-white/10 backdrop-blur-sm px-4 py-2">
              <div className="text-2xl font-bold">{airports.length}</div>
              <div className="text-xs uppercase tracking-widest text-sky-200">Airports</div>
            </div>
            <div className="rounded-lg bg-white/10 backdrop-blur-sm px-4 py-2">
              <div className="text-2xl font-bold">{airlines.length}</div>
              <div className="text-xs uppercase tracking-widest text-sky-200">Airlines</div>
            </div>
          </div>
        </div>
      </div>

      {/* Live aviation news band (AA Phase 3) */}
      {news.length > 0 && (
        <section className="mb-8" data-testid="aviation-live-news">
          <div className="pb-3 mb-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-sky-600" /> Live aviation news
            </h2>
            <Link to="/topic/aviation" className="text-xs uppercase tracking-widest text-blue-700 hover:text-blue-800">
              View all →
            </Link>
          </div>
          <div>
            {news.slice(0, 5).map((e) => <EventCard key={e.event_id} event={e} compact />)}
          </div>
        </section>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Pill active={tab === "airports"} onClick={() => { setTab("airports"); setCountry(""); }}>
          <Plane className="w-3.5 h-3.5" /> Airports
        </Pill>
        <Pill active={tab === "airlines"} onClick={() => { setTab("airlines"); setCountry(""); }}>
          <Building2 className="w-3.5 h-3.5" /> Airlines
        </Pill>
      </div>
      {/* Search + country filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex items-center bg-slate-100 rounded-full pl-5 pr-1 py-1 border border-transparent focus-within:border-blue-500 focus-within:bg-white transition-colors flex-1">
          <SearchIcon className="w-4 h-4 text-slate-400 mr-3" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "airports" ? "Search by name, city, IATA…" : "Search by name, IATA…"}
            className="flex-1 bg-transparent border-0 outline-none text-sm py-2 placeholder:text-slate-400"
            data-testid="hub-filter-input"
          />
        </div>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 focus:border-blue-500 outline-none"
          data-testid="hub-filter-country"
        >
          <option value="">All countries</option>
          {countries.map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-slate-500">Loading directory…</p>
      ) : tab === "airports" ? (
        filteredAirports.length ? (
          <>
            <p className="text-xs text-slate-500 mb-3">{filteredAirports.length} airport{filteredAirports.length === 1 ? "" : "s"}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hub-airports-grid">
              {filteredAirports.map((a) => <AirportCard key={a.airport_id} airport={a} />)}
            </div>
          </>
        ) : (
          <p className="text-slate-500 italic">No airports match your filters.</p>
        )
      ) : (
        filteredAirlines.length ? (
          <>
            <p className="text-xs text-slate-500 mb-3">{filteredAirlines.length} airline{filteredAirlines.length === 1 ? "" : "s"}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hub-airlines-grid">
              {filteredAirlines.map((a) => <AirlineCard key={a.airline_id} airline={a} />)}
            </div>
          </>
        ) : (
          <p className="text-slate-500 italic">No airlines match your filters.</p>
        )
      )}
    </div>
  );
}
